export type ClientPerformancePhase =
  | 'frame'
  | 'replica-application'
  | 'snapshot-interarrival'
  | 'scene-reconciliation'
  | 'terrain-window'
  | 'animation'
  | 'depth-sorting'
  | 'effect-planning'
  | 'effect-scheduling'
  | 'render-submission';

export interface ClientPerformanceMetadata {
  build: string;
  browser: string;
  deviceClass: 'desktop' | 'mobile' | 'unknown';
  powerMode: string;
  viewport: { width: number; height: number };
  devicePixelRatio: number;
}

export type PresentationQualityTier = 'full' | 'reduced' | 'constrained';

export interface PresentationWorkload {
  liveEnemies: number;
  /** Live horde entities with a renderer sprite carrying at least one visual layer. */
  materializedEnemies: number;
  xpGems: number;
  corpses: number;
  corpseCreations?: number;
  pendingEffects: number;
  activeEffects: number;
  effectOperationsPlanned?: number;
  effectOperationsScheduled?: number;
  largeEffectCascades?: number;
  /**
   * Scene-shape counters, present only while detailed workload sampling is on
   * (the benchmarks and the dev diagnostic). Absent means not sampled, never
   * "none" - see `GameRenderer.setDetailedWorkloadSampling` (PRD #826).
   */
  displayObjects?: number;
  mobileLayers?: number;
  huedLayers?: number;
  decodedAssets: number;
  decodedAssetBytes: number;
  terrainPlacements?: number;
  /** Actual visible terrain display objects, independent of logical coverage. */
  activeTerrainSprites?: number;
  /** Logical terrain tiles covered by the visible representation. */
  terrainCoverageTiles?: number;
  staticPlacements?: number;
  activeStaticSprites?: number;
  terrainWindowUpdates?: number;
}

export interface PresentationQualityDecision {
  tier: PresentationQualityTier;
  reason: string;
}

export interface PresentationPerformanceState {
  workload: PresentationWorkload;
  quality: PresentationQualityDecision;
  cadence?: {
    state: 'active' | 'ambient' | 'quiescent';
    fps: number;
  };
}

export interface ClientPerformanceOptions {
  enabled: boolean;
  sampleCapacity?: number;
  metadata: () => ClientPerformanceMetadata;
  now?: () => number;
  observeLongTasks?: (listener: (durationMs: number) => void) => () => void;
}

export interface PhaseDistribution {
  count: number;
  windowCount: number;
  latestMs: number;
  meanMs: number;
  p50Ms: number;
  p95Ms: number;
  p99Ms: number;
  maxMs: number;
}

export interface ClientPerformanceReport {
  metadata: ClientPerformanceMetadata;
  phases: Partial<Record<ClientPerformancePhase, PhaseDistribution>>;
  presentation?: PresentationPerformanceState;
  longTasks?: {
    count: number;
    totalMs: number;
    maxMs: number;
  };
}

export interface PresentationPressureSnapshot {
  frame?: PhaseDistribution;
  longTasks: number;
}

function percentile(sorted: readonly number[], fraction: number): number {
  const rank = Math.max(1, Math.ceil(sorted.length * fraction));
  return sorted[rank - 1]!;
}

function distribution(samples: readonly number[], count: number): PhaseDistribution {
  const sorted = [...samples].sort((a, b) => a - b);
  const total = samples.reduce((sum, value) => sum + value, 0);
  return {
    count,
    windowCount: samples.length,
    latestMs: samples[samples.length - 1]!,
    meanMs: total / samples.length,
    p50Ms: percentile(sorted, 0.5),
    p95Ms: percentile(sorted, 0.95),
    p99Ms: percentile(sorted, 0.99),
    maxMs: sorted[sorted.length - 1]!,
  };
}

export class ClientPerformanceMonitor {
  private readonly samples = new Map<ClientPerformancePhase, number[]>();
  private readonly counts = new Map<ClientPerformancePhase, number>();
  private presentation?: PresentationPerformanceState;
  private longTaskCount = 0;
  private longTaskTotalMs = 0;
  private longTaskMaxMs = 0;
  private readonly disconnectLongTaskObserver?: () => void;

  constructor(private readonly options: ClientPerformanceOptions) {
    if (options.enabled && options.observeLongTasks) {
      this.disconnectLongTaskObserver = options.observeLongTasks((durationMs) => {
        if (!Number.isFinite(durationMs) || durationMs < 50) return;
        this.longTaskCount += 1;
        this.longTaskTotalMs += durationMs;
        this.longTaskMaxMs = Math.max(this.longTaskMaxMs, durationMs);
      });
    }
  }

  measure<T>(phase: ClientPerformancePhase, operation: () => T): T {
    if (!this.options.enabled) return operation();
    const now = this.options.now ?? performance.now.bind(performance);
    const startedAt = now();
    try {
      return operation();
    } finally {
      this.record(phase, now() - startedAt);
    }
  }

  record(phase: ClientPerformancePhase, durationMs: number): void {
    if (!this.options.enabled || !Number.isFinite(durationMs) || durationMs < 0) return;
    const samples = this.samples.get(phase) ?? [];
    samples.push(durationMs);
    const capacity = Math.max(1, Math.floor(this.options.sampleCapacity ?? 600));
    if (samples.length > capacity) samples.splice(0, samples.length - capacity);
    this.samples.set(phase, samples);
    this.counts.set(phase, (this.counts.get(phase) ?? 0) + 1);
  }

  updatePresentation(presentation: PresentationPerformanceState): void {
    if (!this.options.enabled) return;
    this.presentation = {
      workload: { ...presentation.workload },
      quality: { ...presentation.quality },
      ...(presentation.cadence ? { cadence: { ...presentation.cadence } } : {}),
    };
  }

  presentationSnapshot(): PresentationPerformanceState | undefined {
    if (!this.presentation) return undefined;
    return {
      workload: { ...this.presentation.workload },
      quality: { ...this.presentation.quality },
      ...(this.presentation.cadence ? { cadence: { ...this.presentation.cadence } } : {}),
    };
  }

  /**
   * Summarize only the recent frame window used by the live presentation governor.
   *
   * Benchmarks can retain a full soak's samples for their final report. Sorting that
   * growing history once per second would make the observer manufacture the frame
   * pressure it is meant to detect, so the governor always reads a fixed-size tail.
   */
  presentationPressureSnapshot(windowCapacity = 600): PresentationPressureSnapshot {
    const samples = this.samples.get('frame');
    const capacity = Math.max(1, Math.floor(windowCapacity));
    const recent = samples?.slice(-capacity);
    return {
      ...(recent && recent.length > 0
        ? {
            frame: distribution(
              recent,
              this.counts.get('frame') ?? recent.length,
            ),
          }
        : {}),
      longTasks: this.longTaskCount,
    };
  }

  reset(): void {
    this.samples.clear();
    this.counts.clear();
    this.presentation = undefined;
    this.longTaskCount = 0;
    this.longTaskTotalMs = 0;
    this.longTaskMaxMs = 0;
  }

  destroy(): void {
    this.disconnectLongTaskObserver?.();
  }

  report(): ClientPerformanceReport {
    const phases: Partial<Record<ClientPerformancePhase, PhaseDistribution>> = {};
    for (const [phase, samples] of this.samples) {
      if (samples.length > 0) phases[phase] = distribution(samples, this.counts.get(phase) ?? samples.length);
    }
    const presentation = this.presentationSnapshot();
    return {
      metadata: this.options.metadata(),
      phases,
      ...(presentation ? { presentation } : {}),
      ...(this.longTaskCount > 0
        ? {
            longTasks: {
              count: this.longTaskCount,
              totalMs: this.longTaskTotalMs,
              maxMs: this.longTaskMaxMs,
            },
          }
        : {}),
    };
  }
}
